import { convex } from "@/lib/convexClient";
import { api } from "../../convex/_generated/api";

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  approval_status: "pending" | "approved" | "rejected";
  // New schema: separate boolean flags for features
  has_360crafter_cloud: boolean;
  has_byo_s3: boolean;
  byo_s3_purchased_at: string | null;
  cloud_subscription_status: "active" | "inactive" | "expired";
  cloud_subscription_expires_at: string | null;
  // Legacy fields (kept for backward compatibility during migration)
  subscription_type?: "free" | "360crafter_cloud" | "byo_s3";
  subscription_status?: "active" | "inactive" | "expired";
  payment_status?: "none" | "one_time_paid" | "subscription_active";
}

/**
 * Check if user can create a craftspace with the specified storage provider
 */
export const canCreateCraftspaceWithStorage = async (
  _userId: string,
  storageProvider: "360crafter" | "custom-s3",
): Promise<{ canCreate: boolean; reason?: string }> => {
  try {
    const result = await convex.query(api.users.canCreateCraftspaceWithStorage, {
      storageProvider,
    });
    return result;
  } catch (error) {
    console.error("Error checking craftspace permissions:", error);
    return { canCreate: false, reason: "Error checking permissions" };
  }
};

/**
 * Get user's subscription status and available features
 */
export const getUserSubscriptionInfo = (userProfile: UserProfile) => {
  const isApproved = userProfile.approval_status === "approved";

  // Use new schema fields if available, fallback to legacy fields
  const has360CrafterCloud =
    userProfile.has_360crafter_cloud !== undefined
      ? userProfile.has_360crafter_cloud && userProfile.cloud_subscription_status === "active"
      : userProfile.subscription_type === "360crafter_cloud" &&
        userProfile.subscription_status === "active";

  const hasBYOS3 =
    userProfile.has_byo_s3 !== undefined
      ? userProfile.has_byo_s3
      : (userProfile.subscription_type === "byo_s3" &&
          userProfile.subscription_status === "active") ||
        userProfile.payment_status === "one_time_paid";

  return {
    isApproved,
    has360CrafterCloud,
    hasBYOS3,
    canCreate360CrafterCraftspace: isApproved && has360CrafterCloud,
    canCreateBYOS3Craftspace: isApproved && hasBYOS3,
    // Legacy fields for backward compatibility
    subscriptionType: userProfile.subscription_type,
    subscriptionStatus: userProfile.subscription_status,
    paymentStatus: userProfile.payment_status,
    // New fields
    byo_s3_purchased_at: userProfile.byo_s3_purchased_at,
    cloud_subscription_expires_at: userProfile.cloud_subscription_expires_at,
  };
};

/**
 * Get upgrade message for restricted features
 */
export const getUpgradeMessage = (feature: "360crafter_cloud" | "byo_s3") => {
  if (feature === "360crafter_cloud") {
    return {
      title: "Crafter Cloud Subscription Required",
      description: "To create Crafter Cloud craftspaces, you need an active subscription.",
      action: "Upgrade to Crafter Cloud",
    };
  } else {
    return {
      title: "BYO-S3 Access Required",
      description:
        "To create custom S3 craftspaces, you need either a BYO-S3 subscription or one-time payment.",
      action: "Get BYO-S3 Access",
    };
  }
};
